import type {NotificationNode, EditableGraph, Id} from 'src/types/chatbot';
import type {EntityType} from 'src/types/ats-entities';

import * as React from 'react';

import {useReleaseFlag} from 'src/hooks/product-flags';
import useLabelTranslation from 'src/hooks/useLabelTranslation';

import {useAtsVariables, useDraftDecorator, useEditorState} from './reducer';
import {keyBy} from 'src/utils/map';
import {validateNode} from './validation';

import {singleDynamicRegex} from 'src/components/lib/markdown-editor/dynamic-text';
import VariablePickerSelect from 'src/components/lib/variable-picker/select-2';
import {findAncestorNodeVariables} from '../utils';
import EditorContainer from './container';
import {
  UncontrolledDraftEditor,
  prepareForServer,
  nodeVariableDecorator,
} from 'src/components/conversation-builder/draft-editor';
import {FormPair} from 'src/components/conversation-builder/form';
import {PadSection} from './common';

import css from 'src/components/conversation-builder/popover.css';


export default function NotificationEditor({
  graph,
  entityType,
  defaultNode,
  hasSubmitted,
  edgeId,
  ...props
}: {
  graph: EditableGraph;
  entityType: EntityType;
  onClose: () => unknown;
  onSubmit: (arg1: NotificationNode) => unknown;
  defaultNode: NotificationNode;
  actionName: 'add' | 'edit';
  edgeId?: Id;
  hasSubmitted: boolean;
  readOnly: boolean;
}) {
  const {t} = useLabelTranslation();
  const {readOnly} = props;

  const dynamicLabels = useAtsVariables(entityType);
  const decorator = useDraftDecorator(dynamicLabels, [nodeVariableDecorator]);
  const [state, dispatch] = useEditorState({
    defaultNode,
  });

  // @ts-expect-error - TS7006 - Parameter 'payload' implicitly has an 'any' type.
  const update = (payload) => dispatch({type: 'update', payload});
  const {node} = state;

  const emailDynamicLabels = React.useMemo(
    () =>
      dynamicLabels.filter((label) => ['string', 'email'].includes(label.type)),
    [dynamicLabels],
  );
  // @ts-expect-error - TS2554 - Expected 2 arguments, but got 1.
  const dynamicLabelMap = React.useMemo(() => keyBy(dynamicLabels, 'value'));

  const error = hasSubmitted ? validateNode(node, graph) : null;
  const showFileUploadNodeVar = Boolean(
    useReleaseFlag('chatbotFileUploadLinkNotificationEnabled'),
  );
  const nodeVariables = findAncestorNodeVariables(
    graph,
    defaultNode,
    edgeId,
    showFileUploadNodeVar,
  );

  return (
    <EditorContainer onUpdate={update} state={state} error={error} {...props}>
      <PadSection>
        <FormPair label={t('Recipients', 'Recipients')} TagName="div">
          {/* @ts-expect-error - TS2739 - Type '{ allowArbitraryValues: true; classNames: { container: string; }; baseEntityType: EntityType; values: any[]; options: any[]; onChange: (options: any[]) => void; disabled: boolean; }' is missing the following properties from type 'SenseTypeaheadProps<any>': resolveKey, resolveLabel */}
          <VariablePickerSelect
            allowArbitraryValues
            classNames={{container: css.variablePicker}}
            baseEntityType={entityType}
            values={node.notification_recipients.map((recipient) => {
              const match = recipient.match(singleDynamicRegex);
              return (
                (match && dynamicLabelMap.get(match[1])) || {
                  value: recipient,
                  label: recipient,
                  arbitrary: true,
                }
              );
            })}
            options={emailDynamicLabels}
            onChange={(options) => {
              update({
                notification_recipients: options.map((option) =>
                  option.template
                    ? option.template.replace('$', option.value)
                    : option.value,
                ),
              });
            }}
            disabled={readOnly}
          />
        </FormPair>

        <FormPair label={t('Subject', 'Subject')} TagName="div">
          <UncontrolledDraftEditor
            placeholder={`${t('Enter_a_subject', 'Enter a subject')}...`}
            defaultValue={node.notification_email_subject}
            onChange={(editorState) => {
              update({
                notification_email_subject: prepareForServer(
                  editorState,
                  dynamicLabels,
                ),
              });
            }}
            dynamicLabels={dynamicLabels}
            decorator={decorator}
            entityType={entityType}
            isOneLine
            readOnly={readOnly}
            useCase={graph.use_case}
            nodeVariables={nodeVariables}
          />
        </FormPair>

        <FormPair label={t('Message', 'Message')} TagName="div">
          <UncontrolledDraftEditor
            placeholder={`${t('Enter_a_message', 'Enter a message')}...`}
            defaultValue={node.notification_text_content}
            onChange={(editorState) =>
              update({
                notification_text_content: prepareForServer(
                  editorState,
                  dynamicLabels,
                ),
              })
            }
            dynamicLabels={dynamicLabels}
            decorator={decorator}
            entityType={entityType}
            readOnly={readOnly}
            useCase={graph.use_case}
            nodeVariables={nodeVariables}
          />
        </FormPair>
      </PadSection>
    </EditorContainer>
  );
}
